import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import NiceModal, { antdModalV5, useModal } from '@ebay/nice-modal-react';
import { Modal } from 'antd';
import styled from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { intelligenceScopeDeclarations, tokens } from 'containers/Intelligence/theme';
import {
  CardDataState,
  IntelligenceFilters,
  MetricDelta,
  MetricFormat
} from 'containers/Intelligence/types';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import BreakdownTabs, { breakdownTabId, IBreakdownTab } from '../BreakdownTabs';
import CardDataStateBlock from '../CardDataStateBlock';
import CountingRuleNote from '../CountingRuleNote';
import MetricValue from '../MetricValue';
import PeriodDelta from '../PeriodDelta';

/**
 * Everything a breakdown inherits from the card it was opened from, as ONE object. The FR requires
 * the overlay to answer for the same filters, date range, comparison period, permission, consent and
 * freshness as its parent card; the caller passes this object to both `useBreakdownData` and the
 * shell so the two can never derive a different answer from the same page state. PLA-8815.
 */
export interface IBreakdownCardContext {
  /** The originating card's display name, rendered through `Breakdowns.Context`. */
  cardLabel: string;
  /** Threaded to `useBreakdownData`; the shell renders no filter chrome of its own. */
  filters?: IntelligenceFilters;
  /**
   * ISO 8601 including a time component — Safari rejects a bare `YYYY-MM-DD`. Carried for parity
   * with the card's own payload; the shell does not render a freshness stamp of its own (no popup
   * frame draws one — PLA-8815).
   */
  updatedAt?: string | null;
  permitted?: boolean;
  consentGranted?: boolean;
}

export interface IBreakdownHeadline {
  value: number | null;
  format: MetricFormat;
  currency?: string;
  denominator?: string;
  delta?: MetricDelta;
}

export interface IBreakdownModalProps {
  title: string;
  context: IBreakdownCardContext;
  state?: CardDataState;
  headline?: IBreakdownHeadline;
  tabs?: IBreakdownTab[];
  activeTabKey?: string;
  onTabChange?: (key: string) => void;
  /** `BreakdownFixture.countingRule` — the denominator disclosure, always payload. */
  countingRule?: string;
  /** Technical Error only; the seam owner wires the refetch. */
  onRetry?: () => void;
  onClose?: () => void;
  width?: number;
  children?: React.ReactNode;
}

const MODAL_WIDTH = 672;
const CLOSE_BUTTON_SIZE = '34px';

const OK_STATE: CardDataState = { kind: 'ok' };
const PERMISSION_RESTRICTED_STATE: CardDataState = { kind: 'permissionRestricted' };
const CONSENT_RESTRICTED_STATE: CardDataState = { kind: 'consentRestricted' };

// `589:33328`/`589:33967`, read independently on two different popups, agree: this shell's own
// border is `#212D4B`, not `border-subtle` (`#2A344D`) — the nearest existing token, but a
// different one, the same way the filter panel's own palette stayed `filter-border` rather than
// folding into it (AF-3). Kept local rather than promoted to a token pass of its own.
const BREAKDOWN_MODAL_BORDER = tokens.color['filter-border'];
// Read off the same two frames: 31px left/right content inset, not the shared `space['24']` (24px).
const BREAKDOWN_MODAL_SIDE_PADDING = '31px';

// The overlay is portalled to document.body, which leaves the page's token scope behind, so the
// scope is re-declared here on the dialog element every descendant inherits from. The VARIANT has to
// come from the flag as well: a re-declaration pinned to dark is how a light page opened a dark
// overlay, so the dialog reads `usePerx3Redesign` and passes it down like any other style prop.
const BreakdownDialog = styled(Modal)<{ $perx3: boolean }>`
  ${({ $perx3 }) => intelligenceScopeDeclarations($perx3)}

  .ant-modal-content {
    background: ${tokens.color['surface-section']};
    border: 1px solid ${BREAKDOWN_MODAL_BORDER};
    /* radius['16'] (corner radius/16), not radius.lg (18px) — the same distinct Figma variable
       the Growth Planner slide-out shell already reads this token from. */
    border-radius: ${tokens.radius['16']};
    box-shadow: ${tokens.elevation.overlay};
  }

  .ant-modal-header {
    padding: ${tokens.space['24']} 64px ${tokens.space['8']} ${BREAKDOWN_MODAL_SIDE_PADDING};
    background: transparent;
    border-bottom: none;
    border-radius: 0;
  }

  .ant-modal-body {
    padding: ${tokens.space['16']} ${BREAKDOWN_MODAL_SIDE_PADDING} ${tokens.space['24']};
  }
`;

const HeaderText = styled.div`
  > * + * {
    margin-top: ${tokens.space['4']};
  }
`;

/* `589:33328` reads the eyebrow at 12/700/uppercase, tracked 0.72 — off `label-size`/`label-weight`
   (11/600, no tracking), the shared pair every other label on the page still reads correctly.
   Color stays `accent-purple`: `#4F46E5` vs the token is the documented AF-3 hue collapse, not a
   gap this popup should re-open on its own. PLA-8815. */
const Context = styled.p`
  margin: 0;
  color: ${tokens.color['accent-purple']};
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
  letter-spacing: 0.72px;
  text-transform: uppercase;
`;

/* Same frame reads the title at 26/29.9 against the shared `section-title-*` tokens' 27/32.8 —
   those tokens drive every other section heading on the page, so the two literals below are local
   to this one modal, not a change to the scale. */
const Title = styled.h2`
  margin: 0;
  color: ${tokens.color['text-primary']};
  font-size: ${tokens.type['heading2-size']};
  font-weight: ${tokens.type['section-title-weight']};
  line-height: 29.9px;
`;

const Shell = styled.div`
  > * + * {
    margin-top: ${tokens.space['16']};
  }
`;

/* `589:33328`/`589:33477`, both frames: top 21 (off the space scale) / right 24 (space['24']),
   radius 9 (radius['9'], the same distinct value the handoff rail already reads it from) —
   against the previous 16/16/radius.sm(8). PLA-8815. */
const CloseButton = styled.button`
  position: absolute;
  top: 21px;
  right: ${tokens.space['24']};
  width: ${CLOSE_BUTTON_SIZE};
  height: ${CLOSE_BUTTON_SIZE};
  padding: 0;
  border: none;
  border-radius: ${tokens.radius['9']};
  background: ${tokens.color['surface-card-alt']};
  color: ${tokens.color['text-secondary']};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const Headline = styled.div`
  padding: ${tokens.space['24']} ${tokens.space['16']} ${tokens.space['16']};
  border-radius: ${tokens.radius.md};
  background: ${tokens.color['surface-card']};

  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const HeadlineLabel = styled.p`
  margin: 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['label-weight']};
  line-height: ${tokens.type['label-line']};
  text-transform: uppercase;
`;

/**
 * The `View Details` overlay shell: title, the card it was opened from, the current value with its
 * period comparison, a content slot for the breakdown body, and the counting-rule note. Rendered
 * inside `BreakdownModal` below, and directly by a container that owns its own `NiceModal.create`
 * and needs to build the chrome from a payload it has already resolved.
 */
export const BreakdownModalShell: React.FC<IBreakdownModalProps> = ({
  title,
  context,
  state,
  headline,
  tabs,
  activeTabKey,
  onTabChange,
  countingRule,
  onRetry,
  onClose,
  width = MODAL_WIDTH,
  children
}) => {
  const { t } = useMultiLanguageTranslation();
  const modal = useModal();
  const isPerx3 = usePerx3Redesign();
  const { cardLabel, permitted, consentGranted } = context;

  // The authorisation answer precedes the payload: a breakdown the role or the customer's consent
  // does not cover renders its restricted block even when the caller handed over a body to draw.
  let effectiveState = state || OK_STATE;

  if (permitted === false) {
    effectiveState = PERMISSION_RESTRICTED_STATE;
  } else if (consentGranted === false) {
    effectiveState = CONSENT_RESTRICTED_STATE;
  }

  const showsBody = effectiveState.kind === 'ok';
  const views = Array.isArray(tabs) ? tabs : [];
  // A strip nobody listens to would be a dead control, so the views render only once the body has
  // taken ownership of the active key.
  const showsTabs = showsBody && views.length > 0 && typeof onTabChange === 'function';

  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    modal.resolve();
    modal.hide();
  };

  return (
    <BreakdownDialog
      $perx3={isPerx3}
      {...antdModalV5(modal)}
      onCancel={handleClose}
      closable={false}
      footer={null}
      width={width}
      // Closing returns the reader to the card they opened the overlay from, at the scroll position
      // they left (FR § Breakdown pop-ups, verification scenario 3).
      focusTriggerAfterClose
      title={
        <HeaderText>
          <Context data-testid="breakdown-modal-context">
            {t(`${TCustomerInsights}.Breakdowns.Context`, { card: cardLabel })}
          </Context>
          <Title data-testid="breakdown-modal-title">{title}</Title>
        </HeaderText>
      }
    >
      <Shell data-testid="breakdown-modal">
        <CloseButton
          type="button"
          onClick={handleClose}
          aria-label={t(`${TCustomerInsights}.Breakdowns.Close`)}
          data-testid="breakdown-modal-close"
        >
          <CloseOutlined />
        </CloseButton>

        {showsBody && headline ? (
          <Headline data-testid="breakdown-modal-headline">
            <HeadlineLabel>{t(`${TCustomerInsights}.Breakdowns.CurrentValue`)}</HeadlineLabel>
            <MetricValue
              value={headline.value}
              format={headline.format}
              currency={headline.currency}
              denominator={headline.denominator}
            />
            {headline.delta ? (
              <PeriodDelta {...headline.delta} currency={headline.currency} />
            ) : null}
          </Headline>
        ) : null}

        {showsTabs ? (
          <BreakdownTabs items={views} activeKey={activeTabKey} onChange={onTabChange} />
        ) : null}

        <div
          data-testid="breakdown-modal-body"
          role={showsTabs ? 'tabpanel' : undefined}
          aria-labelledby={showsTabs && activeTabKey ? breakdownTabId(activeTabKey) : undefined}
        >
          {showsBody ? (
            children
          ) : (
            <CardDataStateBlock state={effectiveState} onRetry={onRetry} skeletonShape="list" />
          )}
        </div>

        {showsBody ? <CountingRuleNote text={countingRule} /> : null}
      </Shell>
    </BreakdownDialog>
  );
};

// NOTE: this modal stays in the Intelligence family rather than `/components/modals/` because it is
// feature-scoped chrome built on the family's tokens and primitives — `conventions/modals.md`
// reserves that folder for cross-feature modals. PLA-8815.
const BreakdownModal = NiceModal.create(BreakdownModalShell);

export default BreakdownModal;
